// utils/reportUtils.js

/**
 * Checks if the response data contains report-like structure
 * @param {Object} data - The response data from getTulyFlow
 * @returns {boolean} - True if it's a report format
 */
export const isReportData = (data) => {
  if (!data || typeof data !== 'object') {
    return false;
  }

  // Check for key report indicators
  const reportKeys = [
    'report_metadata',
    'implementation_strategy', 
    'ai_tools_selection',
    'budget_analysis',
    'risk_assessment',
    'roi_expectations',
    'executive_summary'
  ];

  // If it has at least 3 of these keys, consider it a report
  const foundKeys = reportKeys.filter(key => data.hasOwnProperty(key));
  return foundKeys.length >= 3;
};

/**
 * Sanitizes report data to ensure required fields exist
 * @param {Object} data - Raw report data
 * @returns {Object} - Sanitized report data with default values
 */
export const sanitizeReportData = (data) => {
  const sanitized = { ...data };

  // Ensure report_metadata exists
  if (!sanitized.report_metadata) {
    sanitized.report_metadata = {
      title: "AI Implementation Report",
      description: "Analysis and implementation strategy",
      date: new Date().toISOString(),
      task_description: ""
    };
  }

  // Ensure other sections have at least empty objects
  const requiredSections = [
    'implementation_strategy',
    'ai_tools_selection', 
    'budget_analysis',
    'risk_assessment',
    'roi_expectations',
    'executive_summary'
  ];

  requiredSections.forEach(section => {
    if (!sanitized[section]) {
      sanitized[section] = {};
    }
  });

  // Ensure arrays exist where expected
  if (!Array.isArray(sanitized.ai_tools_selection)) {
    sanitized.ai_tools_selection = [];
  }

  if (sanitized.implementation_strategy && !Array.isArray(sanitized.implementation_strategy.workflow_steps)) {
    sanitized.implementation_strategy.workflow_steps = [];
  }

  if (sanitized.budget_analysis && !Array.isArray(sanitized.budget_analysis.tools_costs)) {
    sanitized.budget_analysis.tools_costs = [];
  }

  if (sanitized.risk_assessment && !Array.isArray(sanitized.risk_assessment.top_risks)) {
    sanitized.risk_assessment.top_risks = [];
  }

  if (sanitized.roi_expectations && !Array.isArray(sanitized.roi_expectations.key_kpis)) {
    sanitized.roi_expectations.key_kpis = [];
  }

  if (sanitized.executive_summary) {
    if (!Array.isArray(sanitized.executive_summary.key_benefits)) {
      sanitized.executive_summary.key_benefits = [];
    }
    if (!Array.isArray(sanitized.executive_summary.main_challenges)) {
      sanitized.executive_summary.main_challenges = [];
    }
    if (!Array.isArray(sanitized.executive_summary.next_steps)) {
      sanitized.executive_summary.next_steps = [];
    }
  }

  return sanitized;
};